import { loadPerformerDirectory } from '@/performers/loadDirectory';
import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  Card,
  CardContent,
  TextField,
  Button,
  Stack,
  Alert,
  MenuItem,
  FormControlLabel,
  Checkbox,
  CircularProgress,
  Breadcrumbs,
  Link as MuiLink,
  Tooltip,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
} from "@mui/material";
import {
  Home as HomeIcon,
  Event as EventIcon,
  Lightbulb as LightbulbIcon,
  Add as AddIcon,
  Settings as SettingsIcon,
} from "@mui/icons-material";
import { TimePicker } from "@mui/x-date-pickers/TimePicker";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { DateCalendar } from "@mui/x-date-pickers/DateCalendar";
import { PickersDay, type PickersDayProps } from "@mui/x-date-pickers/PickersDay";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { useNavigate, useParams } from "react-router-dom";
import {
  useShowsApi,
  useVenuesApi,
  useStagesApi,
  usePerformersApi,
  useHoldsApi,
  useAppContext,
  useTeamsApi,
} from "@/context/AppContext";
import {
  apiDateToDateOnly,
  dateOnlyToApiDate,
  localDateToDateOnly,
  parseDateOnlyToLocalDate,
} from "@/shows/utils/dateOnly";
import { generateRandomShowData } from "@/shows/utils/testDataGenerator";
import { getVenueStageSelection } from '@/utils/selectionDefaults';
import type {
  PerformerList,
  PrimaryCategoryEnum,
  Status02dEnum,
  ShowTypeEnum,
  VenueList,
  Stage,
} from "api-client/models";
import type { Team } from "api-client/models";

export interface ShowFormData {
  title: string;
  show_date: string;
  show_time: string;
  doors_time: string;
  soundcheck_time: string;
  load_in_time: string;
  status: string;
  show_type: string;
  venue: string;
  stage: string;
  promoter_team: string;
  ticket_price: string;
  ticket_capacity: number | "";
  advance_tickets_sold: number | "";
  door_tickets_sold: number | "";
  comps_given: number | "";
  total_attendance: number | "";
  ticket_revenue: string;
  bar_revenue: string;
  other_revenue: string;
  venue_expenses: string;
  production_expenses: string;
  marketing_expenses: string;
  other_expenses: string;
  show_notes: string;
  settlement_notes: string;
  is_settled: boolean;
}

const STATUS_CHOICES = [
  { value: "tentative", label: "Tentative" },
  { value: "confirmed", label: "Confirmed" },
  { value: "completed", label: "Completed" },
  { value: "settled", label: "Settled" },
  { value: "cancelled", label: "Cancelled" },
];

const SHOW_TYPE_CHOICES = [
  { value: "concert", label: "Concert" },
  { value: "festival", label: "Festival" },
  { value: "private_event", label: "Private Event" },
  { value: "corporate", label: "Corporate" },
  { value: "other", label: "Other" },
];

const QUICK_CREATE_CONFIG_KEY = "show_form_quick_create_config";

interface QuickCreateConfig {
  preferredVenueId: string;
  preferredStageId: string;
  preferredShowType: string;
}

const PERFORMER_CATEGORY_CHOICES: PrimaryCategoryEnum[] = [
  "Band",
  "Solo Artist",
  "DJ",
  "Ensemble",
  "Orchestra",
  "Choir",
  "Comedy",
  "Theater",
  "Dance",
  "Other",
];

const initialFormData: ShowFormData = {
  title: "",
  show_date: "",
  show_time: "",
  doors_time: "",
  soundcheck_time: "",
  load_in_time: "",
  status: "tentative",
  show_type: "concert",
  venue: "",
  stage: "",
  promoter_team: "self",
  ticket_price: "0.00",
  ticket_capacity: "",
  advance_tickets_sold: 0,
  door_tickets_sold: 0,
  comps_given: 0,
  total_attendance: 0,
  ticket_revenue: "0.00",
  bar_revenue: "0.00",
  other_revenue: "0.00",
  venue_expenses: "0.00",
  production_expenses: "0.00",
  marketing_expenses: "0.00",
  other_expenses: "0.00",
  show_notes: "",
  settlement_notes: "",
  is_settled: false,
};

export const ShowForm: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user, team } = useAppContext();
  const [formData, setFormData] = useState<ShowFormData>(initialFormData);
  const [venues, setVenues] = useState<VenueList[]>([]);
  const [stages, setStages] = useState<Stage[]>([]);
  const [venueChoicesLoaded, setVenueChoicesLoaded] = useState(false);
  const [performers, setPerformers] = useState<PerformerList[]>([]);
  const [allTeams, setAllTeams] = useState<Team[]>([]);
  const [selectedPerformerIds, setSelectedPerformerIds] = useState<string[]>([]);
  const [selectedHoldDates, setSelectedHoldDates] = useState<string[]>([]);
  const [holdCalendarDate, setHoldCalendarDate] = useState<Date | null>(new Date());
  const [isHoldDateDragging, setIsHoldDateDragging] = useState(false);
  const [holdDateDragStart, setHoldDateDragStart] = useState<Date | null>(null);
  const [holdDateDragSelection, setHoldDateDragSelection] = useState<string[]>([]);
  const [createPerformerOpen, setCreatePerformerOpen] = useState(false);
  const [quickCreateConfigOpen, setQuickCreateConfigOpen] = useState(false);
  const [newPerformerName, setNewPerformerName] = useState("");
  const [newPerformerCategory, setNewPerformerCategory] =
    useState<PrimaryCategoryEnum>("Other");
  const [newPerformerBio, setNewPerformerBio] = useState("");
  const [createPerformerLoading, setCreatePerformerLoading] = useState(false);
  const [quickCreateConfig, setQuickCreateConfig] = useState<QuickCreateConfig>({
    preferredVenueId: "",
    preferredStageId: "",
    preferredShowType: "concert",
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const showsApi = useShowsApi();
  const venuesApi = useVenuesApi();
  const stagesApi = useStagesApi();
  const performersApi = usePerformersApi();
  const holdsApi = useHoldsApi();
  const teamsApi = useTeamsApi();

  const isEditMode = Boolean(id);

  // Helper function to convert time string (HH:mm) to Date object for TimePicker
  const timeStringToDate = (timeStr: string): Date | null => {
    if (!timeStr) return null;
    const [hours, minutes] = timeStr.split(":");
    const date = new Date();
    date.setHours(parseInt(hours, 10));
    date.setMinutes(parseInt(minutes, 10));
    date.setSeconds(0);
    return date;
  };

  // Helper function to convert Date object to time string (HH:mm)
  const dateToTimeString = (date: Date | null): string => {
    if (!date) return "";
    const hours = date.getHours().toString().padStart(2, "0");
    const minutes = date.getMinutes().toString().padStart(2, "0");
    return `${hours}:${minutes}`;
  };

  useEffect(() => {
    fetchVenuesAndStages();
  }, []);

  useEffect(() => {
    try {
      const savedConfig = localStorage.getItem(`${QUICK_CREATE_CONFIG_KEY}:${team?.id}`);
      if (!savedConfig) {
        return;
      }
      const parsedConfig = JSON.parse(savedConfig) as Partial<QuickCreateConfig>;
      setQuickCreateConfig((prev) => ({
        preferredVenueId: parsedConfig.preferredVenueId || prev.preferredVenueId,
        preferredStageId: parsedConfig.preferredStageId || prev.preferredStageId,
        preferredShowType: parsedConfig.preferredShowType || prev.preferredShowType,
      }));
    } catch {
      // Ignore invalid localStorage payloads and fall back to defaults.
    }
  }, []);

  useEffect(() => {
    if (isEditMode) {
      fetchShow();
    }
  }, [id]);

  useEffect(() => {
    if (!venueChoicesLoaded) return;

    setFormData((prev) => {
      const selection = getVenueStageSelection(venues, stages, prev);
      return selection.venue === prev.venue && selection.stage === prev.stage
        ? prev
        : { ...prev, ...selection };
    });
    setQuickCreateConfig((prev) => {
      const selection = getVenueStageSelection(venues, stages, {
        venue: prev.preferredVenueId,
        stage: prev.preferredStageId,
      });
      return selection.venue === prev.preferredVenueId && selection.stage === prev.preferredStageId
        ? prev
        : { ...prev, preferredVenueId: selection.venue, preferredStageId: selection.stage };
    });
  }, [
    venueChoicesLoaded, venues, stages, formData.venue, formData.stage,
    quickCreateConfig.preferredVenueId, quickCreateConfig.preferredStageId,
  ]);

  const fetchVenuesAndStages = async () => {
    try {
      const [venuesData, stagesData, performersData] = await Promise.all([
        venuesApi.venuesList(),
        stagesApi.stagesList(),
        loadPerformerDirectory(performersApi),
      ]);
      teamsApi.teamsList().then((r) => setAllTeams((r.results || []).filter((item) => item.id === team?.id))).catch(() => {});

      const venuesList = venuesData.results || [];
      const stagesList = stagesData.results || [];
      const performersList = performersData;

      setVenues(venuesList);
      setStages(stagesList);
      setPerformers(performersList);
      setVenueChoicesLoaded(true);
    } catch {
      setError("Failed to load venues, stages, or performers.");
    }
  };

  const saveQuickCreateConfig = () => {
    localStorage.setItem(
      `${QUICK_CREATE_CONFIG_KEY}:${team?.id}`,
      JSON.stringify(quickCreateConfig),
    );
    setQuickCreateConfigOpen(false);
  };

  const getCsrfToken = (): string =>
    document.cookie
      .split("; ")
      .find((row) => row.startsWith("csrftoken="))
      ?.split("=")[1] || "";

  const attachSelectedPerformers = async (showId: string) => {
    if (selectedPerformerIds.length === 0) {
      return;
    }

    const responses = await Promise.all(
      selectedPerformerIds.map(async (performerId, index) => {
        const response = await fetch(`/api/shows/${showId}/add_performer/`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "X-Team-ID": String(team?.id ?? ''),
            "X-CSRFToken": getCsrfToken(),
          },
          credentials: "include",
          body: JSON.stringify({
            performer_id: performerId,
            billing_order: index + 1,
            deal_type: "guarantee",
            guarantee_amount: "",
            percentage: "",
            is_paid: false,
            actual_payment: "",
            notes: "",
          }),
        });

        if (!response.ok) {
          const errorData = await response.text();
          throw new Error(errorData || "Failed to attach performer");
        }
      }),
    );

    return responses;
  };

  const isPastDate = (date: Date): boolean => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    const compareDate = new Date(date);
    compareDate.setHours(0, 0, 0, 0);
    return compareDate < today;
  };

  const normalizeDate = (date: Date): Date =>
    new Date(date.getFullYear(), date.getMonth(), date.getDate());

  const getDateRangeDateOnly = (startDate: Date, endDate: Date): string[] => {
    const start = normalizeDate(startDate).getTime();
    const end = normalizeDate(endDate).getTime();
    const min = Math.min(start, end);
    const max = Math.max(start, end);

    const results: string[] = [];
    const cursor = new Date(min);
    while (cursor.getTime() <= max) {
      if (!isPastDate(cursor)) {
        results.push(localDateToDateOnly(cursor));
      }
      cursor.setDate(cursor.getDate() + 1);
    }

    return results;
  };

  const handleHoldDateMouseDown = (date: Date) => {
    if (isPastDate(date)) {
      return;
    }

    const normalized = normalizeDate(date);
    setIsHoldDateDragging(true);
    setHoldDateDragStart(normalized);
    setHoldDateDragSelection([localDateToDateOnly(normalized)]);
    setHoldCalendarDate(normalized);
  };

  const handleHoldDateMouseEnter = (date: Date) => {
    if (!isHoldDateDragging || !holdDateDragStart || isPastDate(date)) {
      return;
    }

    const normalized = normalizeDate(date);
    setHoldDateDragSelection(getDateRangeDateOnly(holdDateDragStart, normalized));
    setHoldCalendarDate(normalized);
  };

  const commitHoldDateSelection = () => {
    if (!isHoldDateDragging) {
      return;
    }

    if (holdDateDragSelection.length > 0) {
      setSelectedHoldDates((prev) =>
        Array.from(new Set([...prev, ...holdDateDragSelection])).sort((a, b) =>
          a.localeCompare(b),
        ),
      );
    }

    setIsHoldDateDragging(false);
    setHoldDateDragStart(null);
    setHoldDateDragSelection([]);
  };

  useEffect(() => {
    const handleGlobalMouseUp = () => {
      commitHoldDateSelection();
    };

    window.addEventListener("mouseup", handleGlobalMouseUp);
    return () => window.removeEventListener("mouseup", handleGlobalMouseUp);
  }, [isHoldDateDragging, holdDateDragSelection, holdDateDragStart]);

  const removeSelectedHoldDate = (dateOnly: string) => {
    setSelectedHoldDates((prev) => prev.filter((date) => date !== dateOnly));
  };

  const activeHoldDateSet = new Set([...selectedHoldDates, ...holdDateDragSelection]);

  const HoldDateCalendarDay = (props: PickersDayProps) => {
    const dateOnly = localDateToDateOnly(props.day);
    const selected = activeHoldDateSet.has(dateOnly);
    const pastDate = isPastDate(props.day);

    return (
      <PickersDay
        {...props}
        selected={selected}
        disabled={props.disabled || pastDate}
        onMouseDown={(event) => {
          event.preventDefault();
          handleHoldDateMouseDown(props.day);
        }}
        onMouseEnter={() => handleHoldDateMouseEnter(props.day)}
        onMouseUp={() => commitHoldDateSelection()}
        sx={{
          ...(selected
            ? {
                bgcolor: "primary.main",
                color: "primary.contrastText",
                "&:hover, &:focus": {
                  bgcolor: "primary.dark",
                },
              }
            : {}),
        }}
      />
    );
  };

  const createHoldsForShow = async (
    showId: string,
    stageId: string,
    promoterTeamId: number,
    dates: string[],
  ): Promise<{ created: number; failed: string[] }> => {
    const results = await Promise.allSettled(
      dates.map(async (dateOnly) => {
        await holdsApi.holdsCreate({
          holdCreateUpdate: {
            stage: stageId,
            holdDate: dateOnlyToApiDate(dateOnly)!,
            promoterTeam: promoterTeamId,
            show: showId,
            notes: formData.show_notes || "",
          },
        });
      }),
    );

    const failed = results
      .map((result, index) => ({ result, date: dates[index] }))
      .filter((item) => item.result.status === "rejected")
      .map((item) => item.date);

    return {
      created: dates.length - failed.length,
      failed,
    };
  };

  const handleCreatePerformer = async () => {
    if (!newPerformerName.trim()) {
      setError("Performer name is required.");
      return;
    }

    try {
      setCreatePerformerLoading(true);
      setError(null);

      const response = await fetch("/api/performers/", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-Team-ID": String(team?.id ?? ''),
          "X-CSRFToken": getCsrfToken(),
        },
        credentials: "include",
        body: JSON.stringify({
          performer_name: newPerformerName.trim(),
          primary_category: newPerformerCategory,
          bio: newPerformerBio.trim() || "",
          tags: [],
          website: "",
          social_media: {},
          hometown: {},
          technical_rider: "",
          hospitality_rider: "",
          is_active: true,
        }),
      });

      if (!response.ok) {
        let errorData: Record<string, unknown> | null = null;
        try {
          errorData = (await response.json()) as Record<string, unknown>;
        } catch {
          errorData = null;
        }
        const message =
          (Array.isArray(errorData?.performer_name)
            ? String(errorData?.performer_name[0])
            : undefined) ||
          (typeof errorData?.detail === "string" ? errorData.detail : undefined) ||
          "Failed to create performer.";
        throw new Error(message);
      }

      const createdPerformer = (await response.json()) as PerformerList;
      setPerformers((prev) =>
        [...prev, createdPerformer].sort((a, b) =>
          a.performerName.localeCompare(b.performerName),
        ),
      );
      setSelectedPerformerIds((prev) => [...prev, createdPerformer.id]);
      setCreatePerformerOpen(false);
      setNewPerformerName("");
      setNewPerformerCategory("Other");
      setNewPerformerBio("");
    } catch (err) {
      setError(
        err instanceof Error ? err.message : "Failed to create performer.",
      );
    } finally {
      setCreatePerformerLoading(false);
    }
  };

  const fetchShow = async () => {
    if (!id) return;

    try {
      setLoading(true);
      const show = await showsApi.showsRetrieve({ id });
      const firstStage = show.showStages?.[0]?.stage;

      setFormData({
        title: show.title || "",
        show_date: apiDateToDateOnly(show.showDate),
        show_time: show.showTime || "",
        doors_time: show.doorsTime || "",
        soundcheck_time: "", // Not in Show model
        load_in_time: "", // Not in Show model
        status: show.status || "tentative",
        show_type: show.showType || "concert",
        venue: firstStage?.venue || "",
        stage: firstStage?.id || "",
        ticket_price: "0.00", // Not in Show model
        ticket_capacity: "", // Not in Show model
        advance_tickets_sold: 0, // Not in Show model
        door_tickets_sold: 0, // Not in Show model
        comps_given: 0, // Not in Show model
        total_attendance: show.attendance || 0,
        ticket_revenue: show.ticketRevenue || "0.00",
        bar_revenue: show.barRevenue || "0.00",
        other_revenue: show.otherRevenue || "0.00",
        venue_expenses: "0.00", // Part of totalExpenses
        production_expenses: "0.00", // Part of totalExpenses
        marketing_expenses: "0.00", // Part of totalExpenses
        other_expenses: show.totalExpenses || "0.00",
        show_notes: show.notes || "",
        settlement_notes: show.settlementNotes || "",
        is_settled: show.isSettled || false,
        // Promoters belong to holds, not the Show response; retain the form default.
        promoter_team: initialFormData.promoter_team,
      });
    } catch {
      setError("Failed to load show. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setLoading(true);

    try {
      // Validate venue and stage selection
      if (!formData.stage) {
        setError("Please select a stage for this show.");
        setLoading(false);
        return;
      }

      const promoterTeamId =
        formData.promoter_team && formData.promoter_team !== "self"
          ? parseInt(formData.promoter_team, 10)
          : team?.id
            ? Number(team.id)
            : null;

      if (selectedHoldDates.length > 0 && !promoterTeamId) {
        setError("Select a promoter (or use in-house) before creating hold dates.");
        setLoading(false);
        return;
      }

      // Convert form data to API format (camelCase) with proper types
      const showData = {
        title: formData.title,
        showDate: dateOnlyToApiDate(formData.show_date),
        showTime: formData.show_time || undefined,
        doorsTime: formData.doors_time || undefined,
        status: formData.status as Status02dEnum,
        showType: formData.show_type as ShowTypeEnum,
        attendance: formData.total_attendance || undefined,
        ticketRevenue: formData.ticket_revenue || undefined,
        barRevenue: formData.bar_revenue || undefined,
        otherRevenue: formData.other_revenue || undefined,
        totalExpenses: formData.other_expenses || undefined,
        isSettled: formData.is_settled,
        settlementNotes: formData.settlement_notes || undefined,
        notes: formData.show_notes || undefined,
        isActive: true,
        team: team?.id || undefined,
        stages: [formData.stage], // Include selected stage
        promoterTeam: promoterTeamId,
      };

      let show;
      let successMessage = isEditMode
        ? "Show updated successfully"
        : "Show created successfully";
      if (isEditMode) {
        show = await showsApi.showsPartialUpdate({
          id: id!,
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          patchedShowCreateUpdate: showData as any, // OpenAPI schema accepts snake_case
        });
      } else {
        show = await showsApi.showsCreate({
          showCreateUpdate: showData,
        });
        await attachSelectedPerformers(show.id);

        if (selectedHoldDates.length > 0 && promoterTeamId) {
          const holdResults = await createHoldsForShow(
            show.id,
            formData.stage,
            promoterTeamId,
            selectedHoldDates,
          );

          if (holdResults.failed.length > 0) {
            successMessage = `Show created. Holds created for ${holdResults.created}/${selectedHoldDates.length} dates.`;
          } else {
            successMessage = `Show and ${selectedHoldDates.length} hold date${selectedHoldDates.length > 1 ? "s" : ""} created successfully`;
          }
        }
      }

      navigate(`/shows/${show.id}`, {
        state: {
          message: successMessage,
        },
      });
    } catch (err) {
      const errorMessage =
        err instanceof Error
          ? err.message
          : "Failed to save show. Please try again.";
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleChange =
    (field: keyof ShowFormData) => (e: React.ChangeEvent<HTMLInputElement>) => {
      const value =
        e.target.type === "checkbox"
          ? e.target.checked
          : e.target.type === "number"
            ? e.target.value === ""
              ? ""
              : Number(e.target.value)
            : e.target.value;

      setFormData((prev) => ({
        ...prev,
        [field]: value,
      }));
    };

  const handleQuickFill = () => {
    if (!isEditMode) {
      const randomData = generateRandomShowData();
      const selection = getVenueStageSelection(venues, stages, {
        venue: quickCreateConfig.preferredVenueId || formData.venue,
        stage: quickCreateConfig.preferredStageId || formData.stage,
      });
      const dataWithDefaults: ShowFormData = {
        ...initialFormData,
        ...randomData,
        show_type: quickCreateConfig.preferredShowType || randomData.show_type,
        ...selection,
        promoter_team: "self",
      } as ShowFormData;
      setFormData(dataWithDefaults);
    }
  };

  const venueStages = stages.filter((stage) => stage.venue === formData.venue);
  const preferredVenueStages = stages.filter((stage) => stage.venue === quickCreateConfig.preferredVenueId);

  if (loading && isEditMode) {
    return (
      <Stack alignItems="center" mt={4}>
        <CircularProgress />
        <Typography sx={{ mt: 2 }}>Loading show...</Typography>
      </Stack>
    );
  }

  return (
    <Box sx={{ p: { xs: 0, sm: 3 }, overflowWrap: 'anywhere' }}>
      {/* Breadcrumbs */}
      <Breadcrumbs sx={{ mb: 2 }}>
        <MuiLink
          component="button"
          onClick={() => navigate("/")}
          sx={{ display: "flex", alignItems: "center", cursor: "pointer" }}
          underline="hover"
          color="inherit"
        >
          <HomeIcon sx={{ mr: 0.5 }} fontSize="small" />
          Home
        </MuiLink>
        <MuiLink
          component="button"
          onClick={() => navigate("/shows")}
          sx={{ display: "flex", alignItems: "center", cursor: "pointer" }}
          underline="hover"
          color="inherit"
        >
          <EventIcon sx={{ mr: 0.5 }} fontSize="small" />
          Shows
        </MuiLink>
        <Typography
          color="text.primary"
          sx={{ display: "flex", alignItems: "center" }}
        >
          {isEditMode ? "Edit Show" : "New Show"}
        </Typography>
      </Breadcrumbs>

      <Typography variant="h4" gutterBottom>
        {isEditMode ? "Edit Show" : "Create New Show"}
      </Typography>
      <Typography variant="body2" color="text.secondary" mb={3}>
        {isEditMode
          ? "Update show details and financial information"
          : "Create a new show with basic details"}
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      <form onSubmit={handleSubmit}>
        <Stack spacing={3}>
          {/* Basic Information */}
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Basic Information
              </Typography>
              <LocalizationProvider dateAdapter={AdapterDateFns}>
                <Stack spacing={2}>
                  <TextField
                    label="Show Title"
                    value={formData.title}
                    onChange={handleChange("title")}
                    required
                    fullWidth
                    placeholder="e.g., Rock Night with The Band"
                  />

                  <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                    <DatePicker
                      label="Show Date"
                      value={
                        formData.show_date
                          ? parseDateOnlyToLocalDate(formData.show_date)
                          : null
                      }
                      onChange={(newValue) => {
                        setFormData((prev) => ({
                          ...prev,
                          show_date: newValue
                            ? localDateToDateOnly(newValue)
                            : "",
                        }));
                      }}
                      slotProps={{
                        textField: {
                          fullWidth: true,
                          helperText: "Optional for hold requests",
                          error: false, // Prevent error state for empty values
                        },
                      }}
                    />
                    <TimePicker
                      label="Show Time"
                      value={timeStringToDate(formData.show_time)}
                      onChange={(newValue) => {
                        setFormData((prev) => ({
                          ...prev,
                          show_time: dateToTimeString(newValue),
                        }));
                      }}
                      slotProps={{
                        textField: {
                          fullWidth: true,
                          helperText: "Optional for hold requests",
                          error: false, // Prevent error state for empty values
                        },
                      }}
                    />
                  </Stack>

                  <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                    <TextField
                      select
                      label="Status"
                      value={formData.status}
                      onChange={handleChange("status")}
                      required
                      fullWidth
                    >
                      {STATUS_CHOICES.map((option) => (
                        <MenuItem key={option.value} value={option.value}>
                          {option.label}
                        </MenuItem>
                      ))}
                    </TextField>
                    <TextField
                      select
                      label="Show Type"
                      value={formData.show_type}
                      onChange={handleChange("show_type")}
                      required
                      fullWidth
                    >
                      {SHOW_TYPE_CHOICES.map((option) => (
                        <MenuItem key={option.value} value={option.value}>
                          {option.label}
                        </MenuItem>
                      ))}
                    </TextField>
                  </Stack>

                  <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                    <TextField
                      select
                      label="Venue"
                      value={formData.venue}
                      onChange={(e) => {
                        const nextVenueId = e.target.value;
                        setFormData((prev) => ({
                          ...prev,
                          ...getVenueStageSelection(venues, stages, {
                            venue: nextVenueId,
                            stage: prev.stage,
                          }),
                        }));
                      }}
                      required
                      fullWidth
                      disabled={venues.length === 0}
                      helperText={
                        venues.length === 0
                          ? "No venues available. Create a venue first."
                          : ""
                      }
                    >
                      {venues.map((venue) => (
                        <MenuItem key={venue.id} value={venue.id}>
                          {venue.name}
                        </MenuItem>
                      ))}
                    </TextField>
                    <TextField
                      select
                      label="Stage"
                      value={formData.stage}
                      onChange={handleChange("stage")}
                      required
                      fullWidth
                      disabled={!formData.venue || venueStages.length === 0}
                      helperText={
                        !formData.venue
                          ? 'Select a venue first.'
                          : venueStages.length === 0
                            ? 'No stages available for this venue. Create a stage first.'
                            : ''
                      }
                    >
                      {venueStages.map((stage) => (
                        <MenuItem key={stage.id} value={stage.id}>
                          {stage.name}
                        </MenuItem>
                      ))}
                    </TextField>
                  </Stack>


                  <TextField
                    select
                    label="Promoter"
                    value={formData.promoter_team}
                    onChange={handleChange("promoter_team")}
                    fullWidth
                    helperText="Who is promoting this show? Choose 'In-house (self)' for house-run shows."
                  >
                    <MenuItem value="self">In-house (self)</MenuItem>
                    {allTeams.map((team) => (
                      <MenuItem key={team.id} value={String(team.id)}>
                        {team.name}
                      </MenuItem>
                    ))}
                  </TextField>

                  {!isEditMode && (
                    <Card variant="outlined" sx={{ bgcolor: "background.default" }}>
                      <CardContent>
                        <Stack spacing={2}>
                          <Stack
                            direction={{ xs: "column", sm: "row" }}
                            spacing={2}
                            justifyContent="space-between"
                            alignItems={{ xs: "flex-start", sm: "center" }}
                          >
                            <Box>
                              <Typography variant="subtitle1" fontWeight={600}>
                                Performers
                              </Typography>
                              <Typography variant="body2" color="text.secondary">
                                Optional. Select performers now and they will be attached after the show is created.
                              </Typography>
                            </Box>
                            <Button
                              variant="outlined"
                              startIcon={<AddIcon />}
                              onClick={() => setCreatePerformerOpen(true)}
                            >
                              Quick Create Performer
                            </Button>
                          </Stack>

                          <TextField
                            select
                            label="Performers"
                            value={selectedPerformerIds}
                            onChange={(e) => {
                              const value = e.target.value;
                              setSelectedPerformerIds(
                                typeof value === "string" ? value.split(",") : value,
                              );
                            }}
                            SelectProps={{
                              multiple: true,
                              renderValue: (selected) => (
                                <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                                  {(selected as string[]).map((performerId) => {
                                    const performer = performers.find((item) => item.id === performerId);
                                    return (
                                      <Chip
                                        key={performerId}
                                        size="small"
                                        label={performer?.performerName || performerId}
                                      />
                                    );
                                  })}
                                </Stack>
                              ),
                            }}
                            fullWidth
                            helperText={
                              performers.length === 0
                                ? "No performers yet. Use Quick Create Performer to add one."
                                : "Optional. Leave empty if you want to add performers later."
                            }
                          >
                            {performers.map((performer) => (
                              <MenuItem key={performer.id} value={performer.id}>
                                {performer.performerName}
                                {performer.primaryCategory
                                  ? ` (${performer.primaryCategory})`
                                  : ""}
                              </MenuItem>
                            ))}
                          </TextField>
                        </Stack>
                      </CardContent>
                    </Card>
                  )}

                  {!isEditMode && (
                    <Card variant="outlined" sx={{ bgcolor: "background.default" }}>
                      <CardContent>
                        <Stack spacing={2}>
                          <Typography variant="subtitle1" fontWeight={600}>
                            Hold Dates (Optional)
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            Choose each hold date on a phone, or drag across the desktop calendar to select a range.
                          </Typography>

                          <Stack direction={{ xs: "column", md: "row" }} spacing={2}>
                            <Box sx={{ display: { xs: 'block', sm: 'none' } }}>
                              <DatePicker label="Add hold date" value={null} referenceDate={holdCalendarDate || undefined} disablePast
                                onChange={(date) => {
                                  if (!date || !Number.isFinite(date.getTime()) || isPastDate(date)) return;
                                  setHoldCalendarDate(date);
                                  setSelectedHoldDates((previous) => Array.from(new Set([...previous, localDateToDateOnly(date)])).sort());
                                }}
                                slotProps={{ textField: { fullWidth: true, helperText: 'Choose a date to add it. Repeat for multiple dates.' } }} />
                            </Box>
                            <Box sx={{ display: { xs: 'none', sm: 'block' }, border: 1, borderColor: "divider", borderRadius: 1, p: 1 }}>
                              <DateCalendar
                                value={holdCalendarDate}
                                onChange={(nextDate) => setHoldCalendarDate(nextDate)}
                                slots={{
                                  day: HoldDateCalendarDay,
                                }}
                              />
                            </Box>

                            <Stack spacing={1} sx={{ flex: 1 }}>
                              <Typography variant="body2" color="text.secondary">
                                Selected hold dates
                              </Typography>
                              {selectedHoldDates.length === 0 ? (
                                <Typography variant="body2" color="text.secondary">
                                  No hold dates selected.
                                </Typography>
                              ) : (
                                <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
                                  {selectedHoldDates.map((dateOnly) => (
                                    <Chip
                                      key={dateOnly}
                                      label={dateOnly}
                                      onDelete={() => removeSelectedHoldDate(dateOnly)}
                                    />
                                  ))}
                                </Stack>
                              )}
                              {selectedHoldDates.length > 0 && (
                                <Button
                                  variant="text"
                                  size="small"
                                  onClick={() => setSelectedHoldDates([])}
                                  sx={{ alignSelf: "flex-start" }}
                                >
                                  Clear Dates
                                </Button>
                              )}
                            </Stack>
                          </Stack>
                        </Stack>
                      </CardContent>
                    </Card>
                  )}
                </Stack>
              </LocalizationProvider>
            </CardContent>
          </Card>

          {/* Timing Details */}
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Timing Details
              </Typography>
              <LocalizationProvider dateAdapter={AdapterDateFns}>
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                  <TimePicker
                    label="Load-In Time"
                    value={timeStringToDate(formData.load_in_time)}
                    onChange={(newValue) => {
                      setFormData((prev) => ({
                        ...prev,
                        load_in_time: dateToTimeString(newValue),
                      }));
                    }}
                    slotProps={{
                      textField: {
                        fullWidth: true,
                      },
                    }}
                  />
                  <TimePicker
                    label="Soundcheck Time"
                    value={timeStringToDate(formData.soundcheck_time)}
                    onChange={(newValue) => {
                      setFormData((prev) => ({
                        ...prev,
                        soundcheck_time: dateToTimeString(newValue),
                      }));
                    }}
                    slotProps={{
                      textField: {
                        fullWidth: true,
                      },
                    }}
                  />
                  <TimePicker
                    label="Doors Time"
                    value={timeStringToDate(formData.doors_time)}
                    onChange={(newValue) => {
                      setFormData((prev) => ({
                        ...prev,
                        doors_time: dateToTimeString(newValue),
                      }));
                    }}
                    slotProps={{
                      textField: {
                        fullWidth: true,
                      },
                    }}
                  />
                </Stack>
              </LocalizationProvider>
            </CardContent>
          </Card>

          {/* Ticketing Information */}
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Ticketing Information
              </Typography>
              <Stack spacing={2}>
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                  <TextField
                    label="Ticket Price"
                    type="number"
                    value={formData.ticket_price}
                    onChange={handleChange("ticket_price")}
                    fullWidth
                    inputProps={{ step: "0.01", min: "0" }}
                  />
                  <TextField
                    label="Ticket Capacity"
                    type="number"
                    value={formData.ticket_capacity}
                    onChange={handleChange("ticket_capacity")}
                    fullWidth
                    inputProps={{ min: "0" }}
                  />
                </Stack>

                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                  <TextField
                    label="Advance Tickets Sold"
                    type="number"
                    value={formData.advance_tickets_sold}
                    onChange={handleChange("advance_tickets_sold")}
                    fullWidth
                    inputProps={{ min: "0" }}
                  />
                  <TextField
                    label="Door Tickets Sold"
                    type="number"
                    value={formData.door_tickets_sold}
                    onChange={handleChange("door_tickets_sold")}
                    fullWidth
                    inputProps={{ min: "0" }}
                  />
                  <TextField
                    label="Comps Given"
                    type="number"
                    value={formData.comps_given}
                    onChange={handleChange("comps_given")}
                    fullWidth
                    inputProps={{ min: "0" }}
                  />
                </Stack>

                <TextField
                  label="Total Attendance"
                  type="number"
                  value={formData.total_attendance}
                  onChange={handleChange("total_attendance")}
                  fullWidth
                  inputProps={{ min: "0" }}
                />
              </Stack>
            </CardContent>
          </Card>

          {/* Revenue */}
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Revenue
              </Typography>
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                <TextField
                  label="Ticket Revenue"
                  type="number"
                  value={formData.ticket_revenue}
                  onChange={handleChange("ticket_revenue")}
                  fullWidth
                  inputProps={{ step: "0.01", min: "0" }}
                />
                <TextField
                  label="Bar Revenue"
                  type="number"
                  value={formData.bar_revenue}
                  onChange={handleChange("bar_revenue")}
                  fullWidth
                  inputProps={{ step: "0.01", min: "0" }}
                />
                <TextField
                  label="Other Revenue"
                  type="number"
                  value={formData.other_revenue}
                  onChange={handleChange("other_revenue")}
                  fullWidth
                  inputProps={{ step: "0.01", min: "0" }}
                />
              </Stack>
            </CardContent>
          </Card>

          {/* Expenses */}
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Expenses
              </Typography>
              <Stack spacing={2}>
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                  <TextField
                    label="Venue Expenses"
                    type="number"
                    value={formData.venue_expenses}
                    onChange={handleChange("venue_expenses")}
                    fullWidth
                    inputProps={{ step: "0.01", min: "0" }}
                  />
                  <TextField
                    label="Production Expenses"
                    type="number"
                    value={formData.production_expenses}
                    onChange={handleChange("production_expenses")}
                    fullWidth
                    inputProps={{ step: "0.01", min: "0" }}
                  />
                </Stack>

                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                  <TextField
                    label="Marketing Expenses"
                    type="number"
                    value={formData.marketing_expenses}
                    onChange={handleChange("marketing_expenses")}
                    fullWidth
                    inputProps={{ step: "0.01", min: "0" }}
                  />
                  <TextField
                    label="Other Expenses"
                    type="number"
                    value={formData.other_expenses}
                    onChange={handleChange("other_expenses")}
                    fullWidth
                    inputProps={{ step: "0.01", min: "0" }}
                  />
                </Stack>
              </Stack>
            </CardContent>
          </Card>

          {/* Notes */}
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Notes
              </Typography>
              <Stack spacing={2}>
                <TextField
                  label="Show Notes"
                  value={formData.show_notes}
                  onChange={handleChange("show_notes")}
                  multiline
                  rows={3}
                  fullWidth
                  placeholder="General notes about the show"
                />
                <TextField
                  label="Settlement Notes"
                  value={formData.settlement_notes}
                  onChange={handleChange("settlement_notes")}
                  multiline
                  rows={3}
                  fullWidth
                  placeholder="Notes about financial settlement"
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={formData.is_settled}
                      onChange={handleChange("is_settled")}
                    />
                  }
                  label="Mark as settled"
                />
              </Stack>
            </CardContent>
          </Card>

          {/* Actions */}
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} justifyContent="flex-end" alignItems="center">
            {!isEditMode && user?.isSuperuser && (
              <Stack direction="row" spacing={1} sx={{ mr: "auto" }}>
                <Tooltip title="Fill form with random test data (superuser only)">
                  <Button
                    variant="outlined"
                    color="secondary"
                    startIcon={<LightbulbIcon />}
                    onClick={handleQuickFill}
                    disabled={loading}
                  >
                    Quick Create
                  </Button>
                </Tooltip>
                {(venues.length > 1 || stages.length > 1) && (
                  <Tooltip title="Quick Create settings">
                    <IconButton
                      color="secondary"
                      onClick={() => setQuickCreateConfigOpen(true)}
                      disabled={loading}
                    >
                      <SettingsIcon />
                    </IconButton>
                  </Tooltip>
                )}
              </Stack>
            )}
            <Button
              variant="outlined"
              onClick={() => navigate("/shows")}
              disabled={loading}
            >
              Cancel
            </Button>
            <Button type="submit" variant="contained" disabled={loading}>
              {loading
                ? "Saving..."
                : isEditMode
                  ? "Update Show"
                  : "Create Show"}
            </Button>
          </Stack>
        </Stack>
      </form>

      <Dialog
        open={quickCreateConfigOpen}
        onClose={() => setQuickCreateConfigOpen(false)}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>Quick Create Settings</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <Typography variant="body2" color="text.secondary">
              Save preferred defaults for Quick Create when your team has multiple venues or stages.
            </Typography>
            <TextField
              select
              label="Preferred Venue"
              value={quickCreateConfig.preferredVenueId}
              onChange={(e) => {
                const nextVenueId = e.target.value;
                setQuickCreateConfig((prev) => {
                  const selection = getVenueStageSelection(venues, stages, {
                    venue: nextVenueId,
                    stage: nextVenueId ? prev.preferredStageId : '',
                  });
                  return {
                    ...prev,
                    preferredVenueId: selection.venue,
                    preferredStageId: selection.stage,
                  };
                });
              }}
              fullWidth
              helperText="Choose a preferred venue when more than one is available."
            >
              <MenuItem value="">Automatic</MenuItem>
              {venues.map((venue) => (
                <MenuItem key={venue.id} value={venue.id}>
                  {venue.name}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              select
              label="Preferred Stage"
              value={quickCreateConfig.preferredStageId}
              onChange={(e) =>
                setQuickCreateConfig((prev) => ({
                  ...prev,
                  preferredStageId: e.target.value,
                }))
              }
              fullWidth
              disabled={!quickCreateConfig.preferredVenueId || preferredVenueStages.length === 0}
              helperText={
                !quickCreateConfig.preferredVenueId
                  ? 'Select a preferred venue first.'
                  : preferredVenueStages.length === 0
                    ? 'No stages available for this venue.'
                    : 'Optional. Stages are filtered by the selected preferred venue.'
              }
            >
              <MenuItem value="">Automatic</MenuItem>
              {preferredVenueStages.map((stage) => (
                <MenuItem key={stage.id} value={stage.id}>
                  {stage.name} ({stage.venueName})
                </MenuItem>
              ))}
            </TextField>
            <TextField
              select
              label="Preferred Show Type"
              value={quickCreateConfig.preferredShowType}
              onChange={(e) =>
                setQuickCreateConfig((prev) => ({
                  ...prev,
                  preferredShowType: e.target.value,
                }))
              }
              fullWidth
            >
              {SHOW_TYPE_CHOICES.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </TextField>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setQuickCreateConfigOpen(false)}>Cancel</Button>
          <Button onClick={saveQuickCreateConfig} variant="contained">
            Save Settings
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={createPerformerOpen}
        onClose={() => {
          if (!createPerformerLoading) {
            setCreatePerformerOpen(false);
          }
        }}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>Quick Create Performer</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField
              label="Performer Name"
              value={newPerformerName}
              onChange={(e) => setNewPerformerName(e.target.value)}
              required
              fullWidth
              autoFocus
            />
            <TextField
              select
              label="Primary Category"
              value={newPerformerCategory}
              onChange={(e) =>
                setNewPerformerCategory(e.target.value as PrimaryCategoryEnum)
              }
              fullWidth
            >
              {PERFORMER_CATEGORY_CHOICES.map((option) => (
                <MenuItem key={option} value={option}>
                  {option}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              label="Bio"
              value={newPerformerBio}
              onChange={(e) => setNewPerformerBio(e.target.value)}
              fullWidth
              multiline
              minRows={3}
              placeholder="Optional short description"
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setCreatePerformerOpen(false)}
            disabled={createPerformerLoading}
          >
            Cancel
          </Button>
          <Button
            onClick={() => void handleCreatePerformer()}
            variant="contained"
            disabled={createPerformerLoading || !newPerformerName.trim()}
          >
            {createPerformerLoading ? "Creating..." : "Create Performer"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};
